import { CircularProgress, TextField, withStyles } from '@material-ui/core';
import { ImportExport } from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { PATHS } from 'constants/paths';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { WellInfo, WellInfoForSite } from 'types/wellGuardian';

import styles from './SelectWell.module.scss';

type WellDetail = {
  wellName: string;
  wellViewId: string;
};

const WhiteColoredTextField = withStyles({
  root: {
    '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input:first-child': {
      color: styles.white,
      fontWeight: 'bold',
    },
  },
})(TextField);

interface WellHeaderProps {
  wellsForSite: WellInfoForSite[];
  wellsForSiteLoading: boolean;
  wellInfo: WellInfo | null;
}

const SelectWell: React.FunctionComponent<WellHeaderProps> = ({
  wellsForSite,
  wellsForSiteLoading,
  wellInfo,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [isSelectWellSort, setIsSelectWellSort] = useState<boolean>(true);

  const [wellDetail, setWellDetail] = useState<WellDetail>({
    wellName: '',
    wellViewId: '',
  });

  const wellsForSiteSorted = useMemo(() => {
    return wellsForSite.sort(
      (a, b) => (isSelectWellSort ? 1 : -1) * (a?.wellName ?? '').localeCompare(b?.wellName ?? ''),
    );
  }, [isSelectWellSort, wellsForSite]);

  const handleWellChange = useCallback(
    (well: WellInfo | null) => {
      if (!well) return;

      if (well?.wellViewId && wellInfo?.wellViewId) {
        history.push(location?.pathname.replace(wellInfo?.wellViewId, well?.wellViewId));
      } else {
        history.push(
          generatePath(PATHS.WELL_GUARDIAN_WELL_SINGLE_OVERVIEW, { tuwi: well?.wellViewId }),
        );
      }
    },
    [history, location?.pathname, wellInfo?.wellViewId],
  );

  useEffect(() => {
    if (
      wellsForSite.length === 0 ||
      wellsForSite.find(well => well?.wellViewId === wellInfo?.wellViewId)
    ) {
      setWellDetail({
        wellName: wellInfo?.wellName ?? '',
        wellViewId: wellInfo?.wellViewId ?? '',
      });
    } else {
      setWellDetail({
        wellName: wellsForSite[0]?.wellName ?? '',
        wellViewId: wellsForSite[0]?.wellViewId ?? '',
      });
      handleWellChange(wellsForSite[0]);
    }
  }, [handleWellChange, wellInfo, wellsForSite]);

  return (
    <Autocomplete
      id="select-well-id"
      data-testid="select-well-id"
      className={styles.selectWellRoot}
      classes={{
        endAdornment: styles.adornmentTextColor,
        paper: styles.autocompleteDropdown,
        option: styles.listOption,
      }}
      options={wellsForSiteSorted}
      autoHighlight
      getOptionLabel={option => (option.wellName ? option.wellName : '')}
      value={wellDetail}
      onChange={(_event, well) => handleWellChange(well)}
      getOptionSelected={(option, value) => option.wellViewId === value.wellViewId}
      renderInput={params =>
        wellsForSiteLoading ? (
          <CircularProgress
            data-testid="select-well-loader"
            size={24}
            className={styles.selectWellLoading}
          />
        ) : (
          <div className={styles.selectWellDropdown}>
            <WhiteColoredTextField
              {...params}
              inputProps={{
                ...params.inputProps,
              }}
              size="small"
              className={styles.selectWellText}
            />
            <ImportExport
              data-testid="select-well-sort"
              className={styles.selectWellSort}
              onClick={() => setIsSelectWellSort(prevState => !prevState)}
            />
          </div>
        )
      }
    />
  );
};

export default SelectWell;
